import { AlertTriangleIcon, CheckCircle2Icon, Loader2, SendIcon } from "lucide-react"
import { useEffect, useId, useRef } from "react"

import { Turnstile } from "@/cloudflare/turnstile"
import type { Locale } from "@/core/i18n/locale"
import { useLocale } from "@/core/i18n/locale-context"
import { useFormAction } from "@/form"
import { Alert, AlertDescription } from "@/ui/alert"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

import { contactFormAction } from "../rpc"
import { contactFormSchema, MESSAGE_MAX } from "../schema"
import { CONTACT_TEXT, localizeMessage } from "./contact-text"

export function ContactForm({
  cloudflareTurnstilePublicKey,
  className,
}: {
  cloudflareTurnstilePublicKey?: string
  className?: string
}) {
  const id = useId()
  const locale = useLocale()
  const text = CONTACT_TEXT[locale]
  const { data, error, isPending, onSubmit, fields } = useFormAction(
    contactFormSchema,
    contactFormAction,
  )
  // Set after mount so a prerendered page never carries a stale timestamp.
  const fillMsRef = useRef<HTMLInputElement>(null)
  const mountedAt = useRef(0)
  useEffect(() => {
    mountedAt.current = Date.now()
  }, [])
  const successRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const alertRef = useRef<HTMLDivElement>(null)
  const success = data?.success
  // After a failed send, move focus to the first invalid field, else to the error message.
  useEffect(() => {
    if (!error) return
    const invalid = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')
    ;(invalid ?? alertRef.current)?.focus()
  }, [error])
  useEffect(() => {
    if (success) successRef.current?.focus()
  }, [success])

  if (success) {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        role="status"
        className={cn(
          "border-border bg-card grid gap-2 rounded-lg border p-6 outline-none",
          className,
        )}
      >
        <p className="flex items-center gap-2 text-lg font-semibold">
          <CheckCircle2Icon aria-hidden className="size-5" />
          {text.sent}
        </p>
        <p className="text-muted-foreground">{localizeMessage(success, locale)}</p>
      </div>
    )
  }

  const field = (name: "name" | "email" | "message") => {
    const errors = fields[name].errors
    return {
      id: `${id}-${name}`,
      name,
      required: true,
      disabled: isPending,
      "aria-invalid": errors.length > 0 || undefined,
      "aria-describedby": errors.length > 0 ? `${id}-${name}-error` : undefined,
      errors,
    }
  }
  const nameField = field("name")
  const emailField = field("email")
  const messageField = field("message")
  return (
    <form
      ref={formRef}
      method="post"
      onSubmit={(event) => {
        if (fillMsRef.current) fillMsRef.current.value = String(Date.now() - mountedAt.current)
        onSubmit(event)
      }}
      noValidate
      className={cn("grid gap-5", className)}
    >
      <div role="alert" ref={alertRef} tabIndex={-1} className="outline-none">
        {error?.message && (
          <Alert variant="destructive">
            <AlertTriangleIcon />
            <AlertDescription>{localizeMessage(error.message, locale)}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor={nameField.id}>{text.name}</Label>
        <Input {...omitErrors(nameField)} autoComplete="name" maxLength={100} />
        <FieldError f={nameField} locale={locale} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={emailField.id}>{text.email}</Label>
        <Input {...omitErrors(emailField)} type="email" autoComplete="email" maxLength={254} />
        <FieldError f={emailField} locale={locale} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={messageField.id}>{text.message}</Label>
        <textarea
          {...omitErrors(messageField)}
          rows={7}
          maxLength={MESSAGE_MAX}
          className="border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive flex w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
        />
        <FieldError f={messageField} locale={locale} />
      </div>

      {/* Honeypot and fill-time trap: hidden from people and screen readers. */}
      <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>
          Leave this empty
          <input name="hp_k7x2" type="text" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <input ref={fillMsRef} type="hidden" name="fillMs" defaultValue="" />

      {cloudflareTurnstilePublicKey ? (
        <Turnstile
          siteKey={cloudflareTurnstilePublicKey}
          subjectKey={isPending ? "pending" : "idle"}
        />
      ) : null}

      <div>
        <Button type="submit" disabled={isPending} className="min-h-11">
          {isPending ? (
            <Loader2 aria-hidden className="size-4 animate-spin" />
          ) : (
            <SendIcon aria-hidden className="size-4" />
          )}
          {text.send}
        </Button>
      </div>
    </form>
  )
}

function FieldError({ f, locale }: { f: { id: string; errors: string[] }; locale: Locale }) {
  return f.errors.length > 0 ? (
    <p id={`${f.id}-error`} className="text-destructive text-sm">
      {localizeMessage(f.errors[0] ?? "", locale)}
    </p>
  ) : null
}

function omitErrors<T extends { errors: string[] }>({ errors: _errors, ...rest }: T) {
  return rest
}
